import { useMemo } from 'react';

import { Input } from '@/components/form/Input';
import WebhookExample from '@/components/legacy-ui/WebhookExample';
import { INTEGRATION_CATEGORIES } from '@/features/integrations/config/categories.config';
import {
  BitbucketAuthInstructions,
  GithubInstructions,
} from '@/hooks/integrationInstructions';
import { useMy } from '@/hooks/useMy';
import { AWS } from '@/sections/integrations/AWS';
import { Azure } from '@/sections/integrations/Azure';
import { GoogleCloud } from '@/sections/integrations/GoogleCloud';
import {
  Account,
  availableNotifications,
  Integration,
  IntegrationMeta,
} from '@/types';
import { UseExtendInfiniteQueryOptions } from '@/utils/api';
import { generateUuid } from '@/utils/uuid.util';

export type AccountWithType = Account & { type: string; displayName: string };

export const generateSeverityOptions = () => {
  return [
    { label: 'Critical', value: 'C' },
    { label: 'High', value: 'HC' },
    { label: 'Medium', value: 'MHC' },
    { label: 'Low', value: 'LMHC' },
    { label: 'Info', value: 'ILMHC' },
  ];
};

const severityInfo = {
  text: 'Select the minimum risk severity level at which Chariot should send notifications.',
};

const defaultPin = generateUuid();
const uuid = generateUuid();
const options = generateSeverityOptions();

export const AllIntegrations: Record<Integration, IntegrationMeta> = {
  servicenow: {
    id: Integration.servicenow,
    name: 'ServiceNow',
    description:
      "Transform IT operations with ServiceNow's ITSM platform, delivering automated workflows, incident resolution, and robust asset management capabilities.",
    logo: 'servicenow.svg',
    inputs: [
      {
        name: 'username',
        value: Integration.webhook,
        hidden: true,
      },
      {
        name: 'value',
        value: 'servicenow',
        hidden: true,
      },
      {
        label: 'Scripted REST API URL',
        value: '',
        placeholder:
          'https://your-company.service-now.com/api/snc/praetorian_chariot_webhook/notification',
        name: 'webhook',
        required: true,
        info: {
          text: 'The URL of your scripted REST API endpoint',
        },
      },
      {
        label: 'Basic Authentication Credentials',
        value: 'Authorization',
        placeholder: 'Content-Type: application/json',
        name: 'header',
        required: true,
        disabled: true,
        hidden: true,
      },
      {
        label: 'Basic Auth Token',
        value: '',
        placeholder: 'Basic QmFzaWMgQXV0aCBUb2tlbgo=',
        name: 'token',
        required: false,
        info: {
          text: 'Optional, the HTTP Basic Auth value for the Authorization header',
        },
      },
      {
        label: 'Severity',
        value: 'MHC',
        name: 'severities',
        required: true,
        type: Input.Type.SELECT,
        options: options,
        info: severityInfo,
      },
    ],
  },
  webhook: {
    id: Integration.webhook,
    name: 'Outgoing Webhook',
    description: 'Send notifications for open risks to a webhook.',
    logo: 'webhook.svg',
    inputs: [
      {
        name: 'username',
        value: Integration.webhook,
        hidden: true,
      },
      {
        label: 'Webhook Name',
        name: 'value',
        value: '',
        required: true,
        placeholder: 'SecOps SQS Queue',
        hidden: false,
      },
      {
        label: 'Webhook URL',
        value: '',
        placeholder: 'https://example.com/hooks/apiubav784adfjb1238',
        name: 'webhook',
        required: true,
        info: {
          text: 'The URL of your Webhook',
        },
      },
      {
        label: 'Authentication Header Name',
        value: '',
        placeholder: 'Authorization',
        name: 'header',
        required: false,
        hidden: false,
        info: {
          text: 'Optional, the authentication header name to send',
        },
      },
      {
        label: 'Authentication Header Value',
        value: '',
        placeholder: 'Basic QmFzaWMgQXV0aCBUb2tlbgo=',
        name: 'token',
        required: false,
        info: {
          text: 'Optional, the authentication header value to send',
        },
      },
      {
        label: 'Severity',
        value: 'MHC',
        name: 'severities',
        required: true,
        type: Input.Type.SELECT,
        options: options,
        info: severityInfo,
      },
    ],
  },
  hackerone: {
    id: Integration.hackerone,
    logo: 'hackerone.svg',
    name: 'HackerOne',
    description:
      'Import vulnerability reports from HackerOne bug bounty platform to track disclosed vulnerabilities and security researcher findings.',
    validate: true,
    multiple: true,
    inputs: [
      {
        name: 'username',
        value: 'hackerone',
        hidden: true,
      },
      {
        label: 'API Identifier',
        value: '',
        placeholder: 'Enter your API identifier',
        name: 'value',
        required: true,
      },
      {
        label: 'API Token',
        value: '',
        type: Input.Type.PASSWORD,
        placeholder: 'Enter your API token',
        name: 'api_token',
        required: true,
      },
    ],
  },
  hook: {
    id: Integration.hook,
    name: 'Webhook',
    description:
      'Allows ingestion of assets and vulnerabilities into Chariot via webhooks, facilitating automated data transfers from external sources.',
    logo: 'logo.png',
    help: {
      href: 'https://docs.praetorian.com/articles/25815155351835-chariot-webhook',
      label: 'How to: Chariot Webhooks',
    },
    inputs: [
      {
        name: 'username',
        value: 'hook',
        hidden: true,
      },
      {
        label: 'Secret Pin',
        value: defaultPin,
        placeholder: uuid,
        name: 'pin',
        hidden: true,
      },
    ],
    Markup: () => <WebhookExample defaultPin={defaultPin} />,
  },
  slack: {
    id: Integration.slack,
    name: 'Slack',
    help: {
      href: 'https://docs.praetorian.com/articles/25815125222171-slack',
      label: 'How to: Workplace Messaging - Slack',
    },
    description:
      'Sends notifications directly to Slack channels when new risks are discovered, ensuring timely awareness among your teams.',
    logo: 'slack.svg',
    inputs: [
      {
        name: 'username',
        value: 'slack',
        hidden: true,
      },
      {
        label: 'Webhook URL',
        value: '',
        placeholder: 'https://hooks.slack.com/services/',
        name: 'webhook',
        required: true,
        info: {
          url: 'https://api.slack.com/messaging/webhooks',
          text: 'Learn more',
        },
      },
      {
        label: 'Severity',
        value: 'MHC',
        name: 'severities',
        required: true,
        type: Input.Type.SELECT,
        options: options,
        info: severityInfo,
      },
    ],
  },
  splunk: {
    id: Integration.splunk,
    logo: 'splunk.svg',
    name: 'Splunk Cloud Platform (Preview)',
    description:
      'Integrate with Splunk Cloud to import security events and alerts for enhanced threat visibility.',
    validate: false,
    inputs: [
      {
        name: 'username',
        value: 'splunk',
        hidden: true,
      },
      {
        label: 'Splunk Cloud URL',
        name: 'value',
        value: '',
        placeholder: 'https://your-instance.splunkcloud.com',
        required: true,
      },
      {
        label: 'Authentication Token',
        name: 'auth_token',
        value: '',
        placeholder: 'Enter your Splunk authentication token',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          url: 'https://docs.splunk.com/Documentation/SplunkCloud/latest/Security/CreateAuthTokens',
          text: 'Create a token in Settings > Tokens',
        },
      },
    ],
  },
  email: {
    id: Integration.email,
    name: 'Email',
    description:
      'Delivers email notifications for newly discovered risks, keeping your teams informed of critical vulnerabilities.',
    logo: 'email.svg',
    inputs: [
      {
        name: 'username',
        value: 'email',
        hidden: true,
      },
      {
        label: 'Email Address',
        value: '',
        placeholder: 'example@companydomain.com',
        name: 'email',
        required: true,
      },
      {
        label: 'Severity',
        value: 'MHC',
        name: 'severities',
        required: true,
        type: Input.Type.SELECT,
        options: options,
        info: severityInfo,
      },
    ],
  },
  confluence: {
    id: Integration.confluence,
    name: 'Confluence Cloud',
    description:
      'Scan Confluence Cloud spaces and pages for exposed secrets across all page versions.',
    logo: 'confluence.svg',
    validate: true,
    inputs: [
      {
        name: 'username',
        value: 'confluence',
        hidden: true,
      },
      {
        label: 'Base URL',
        value: '',
        placeholder: 'https://<your-domain>.atlassian.net',
        name: 'value',
        required: true,
      },
      {
        label: 'User Email',
        value: '',
        placeholder: 'username@yourdomain.com',
        name: 'userEmail',
        required: true,
      },
      {
        label: 'Access Token',
        value: '',
        placeholder: 'Your Atlassian API token',
        name: 'accessToken',
        type: Input.Type.TEXT_AREA,
        password: true,
        required: true,
        info: {
          url: 'https://support.atlassian.com/atlassian-account/docs/manage-api-tokens-for-your-atlassian-account/',
          text: 'Learn more',
        },
      },
    ],
  },
  jira: {
    id: Integration.jira,
    multiple: true,
    name: 'Jira Cloud',
    ticketing: true,
    ticketAttributeName: 'jira',
    projectSearchPlaceholder: 'Search for a Jira project...',
    help: {
      href: 'https://docs.praetorian.com/articles/30041121138075-jira',
      label: 'How to: Ticketing Systems - Jira',
    },
    description:
      'Streamline IT workflows and incident management with Jira Cloud, offering customizable service desks, agile project tracking, and seamless integrations.',
    logo: 'jira.svg',
    edit: true,
    inputs: [
      {
        name: 'username',
        value: 'jira',
        hidden: true,
      },
      {
        label: 'Base URL',
        value: '',
        placeholder: 'https://<your-domain>.atlassian.net',
        name: 'value',
        required: true,
      },
      {
        label: 'User Email',
        value: '',
        placeholder: 'username@yourdomain.com',
        name: 'userEmail',
        required: true,
      },
      {
        label: 'Access Token',
        value: '',
        placeholder:
          'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJodHRwczovL2V4YW1wbGUuYXV0aDAuY29tLyIsImF1ZCI6Imh0dHBzOi8vYXBpLmV4YW1wbGUuY29tL2NhbGFuZGFyL3YxLyIsInN1YiI6InVzcl8xMjMiLCJpYXQiOjE0NTg3ODU3OTYsImV4cCI6MTQ1ODg3MjE5Nn0.CA7eaHjIHz5NxeIJoFK9krqaeZrPLwmMmgI_XiQiIkQ',
        name: 'accessToken',
        type: Input.Type.TEXT_AREA,
        password: true,
        required: true,
        info: {
          url: 'https://support.atlassian.com/atlassian-account/docs/manage-api-tokens-for-your-atlassian-account/',
          text: 'Learn more',
        },
      },
    ],
    configuration: {
      title: 'Configuration Jira Settings',
      description: 'Setup project and issue type',
      inputs: [
        {
          label: 'Integration Name',
          value: '',
          placeholder: 'Praetorian Labs',
          name: 'displayName',
          required: true,
        },
        {
          label: 'Project',
          value: '',
          name: 'projectKey',
          placeholder: 'Select Project',
          type: Input.Type.SELECT,
          options: [],
          info: {
            text: 'Learn more',
            content: (
              <>
                <p>
                  The project key is the letters prefix of your the issue key.
                  For example, if your JIRA issues have keys like CHARIOT-6312
                  or CHARIOT-823, the project key is &quot;CHARIOT&quot;.
                </p>
              </>
            ),
          },
          required: true,
        },
        {
          label: 'Project Name',
          value: '',
          name: 'projectName',
          hidden: true,
        },
        {
          label: 'id',
          value: '',
          name: 'id',
          hidden: true,
        },
        {
          label: 'Type',
          value: '',
          name: 'issueType',
          placeholder: 'Select Issue Type',
          dependsOn: 'projectKey',
          type: Input.Type.SELECT,
          options: [],
          info: {
            text: 'Learn more',
            content: (
              <>
                <p>
                  Example issue types are Bug, Security Task, etc. Space in the
                  issue type is allowed.
                </p>
              </>
            ),
          },
          required: true,
        },
        {
          label: 'Automatically Create Jira Issues?',
          info: {
            text: 'When enabled, Jira issues will be automatically created for new Chariot vulnerabilities',
          },
          value: 'false',
          name: 'autoCreate',
          type: Input.Type.CHECKBOX,
        },
        {
          label: 'Vulnerability Severity Threshold',
          info: {
            text: 'Only vulnerabilities with severity at or above the selected threshold will automatically create Jira issues',
          },
          value: 'HC',
          name: 'severities',
          type: Input.Type.SELECT,
          placeholder: 'Select Severity',
          options: options,
          required: true,
          dependsOn: 'autoCreate',
          hideTillDependsOn: true,
        },
      ],
      permissions: {
        BROWSE_PROJECTS: {
          key: 'BROWSE_PROJECTS',
          name: 'Browse Projects',
          description:
            'Ability to browse JIRA projects based on token perms from Chariot.',
          permission: 'Read Only',
          havePermission: true,
        },
        CREATE_ISSUES: {
          key: 'CREATE_ISSUES',
          name: 'Create Issues',
          description: 'Ability to create JIRA tickets from Chariot.',
          permission: 'Write Access',
          havePermission: true,
        },
        EDIT_ISSUES: {
          key: 'EDIT_ISSUES',
          name: 'Edit Issues',
          description: 'Ability to edit JIRA tickets from Chariot.',
          permission: 'Write Access',
          havePermission: true,
        },
        ADD_COMMENTS: {
          key: 'ADD_COMMENTS',
          name: 'Create Comment',
          description:
            'Ability to add a comment to a JIRA ticket from Chariot.',
          permission: 'Write Access',
          havePermission: true,
        },
        RESOLVE_ISSUES: {
          key: 'RESOLVE_ISSUES',
          name: 'Resolve Ticket',
          description: 'Ability to close a ticket in JIRA from Chariot.',
          permission: 'Write Access',
          havePermission: true,
        },
      },
    },
    transitions: {
      title: 'Transitions',
      description:
        'Configure custom workflow transitions for opening and closing tickets.',
      requiresField: 'projectKey',
      disabledTooltip: 'Select a project first to configure transitions',
    },
  },
  github: {
    id: Integration.github,
    name: 'GitHub',
    help: {
      href: 'https://docs.praetorian.com/articles/25815083333787-github',
      label: 'How to: Source Code Managers - GitHub',
    },
    description:
      'Streamline development with GitHub’s industry-leading platform for hosting, versioning, and collaborating on code, combined with robust CI/CD and security capabilities.',
    logo: 'github.svg',
    multiple: true,
    validate: true,
    inputs: [
      {
        name: 'username',
        value: 'github',
        hidden: true,
      },
      {
        label: 'Personal Access Token (PAT)',
        value: '',
        placeholder: 'github_pat_123456abcdefg_123456abcdefg',
        name: 'pat',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          url: 'https://docs.github.com/en/authentication/keeping-your-account-and-data-secure/managing-your-personal-access-tokens',
          text: 'Learn more',
        },
      },
      {
        label: 'GitHub Organization',
        value: '',
        placeholder: 'github.com/organization',
        pattern: '^(http(s)?://)?[^/]+/[^/]+$',
        name: 'value',
        required: true,
        info: {
          url: 'https://docs.github.com/en/organizations/collaborating-with-groups-in-organizations/about-organizations',
          text: 'Learn more',
        },
      },
    ],
    Markup: () => <GithubInstructions />,
  },
  amazon: {
    id: Integration.amazon,
    name: 'AWS',
    description:
      'Discovers assets hosted within your AWS environment, enabling visibility into cloud infrastructure and potential vulnerabilities.',
    logo: 'aws.svg',
    multiple: true,
    edit: true,
    Markup: AWS,
    hideFooter: true,
  },
  legit: {
    id: Integration.legit,
    logo: 'legit.png',
    name: 'Legit Security',
    description:
      'Secure your software supply chain and development lifecycle with automated posture management and threat detection.',
  },
  apiiro: {
    id: Integration.apiiro,
    logo: 'apiiro.png',
    description:
      'Gain visibility and control over application risks from design to production with a unified platform.',
    name: 'Apiiro',
  },
  ns1: {
    id: Integration.ns1,
    name: 'NS1',
    help: {
      href: 'https://docs.praetorian.com/articles/28054661327771-ns1-connect',
      label: 'How to: Asset Ingestion - NS1',
    },
    description:
      "Enhance application performance and availability with NS1's managed DNS, featuring intelligent traffic routing and advanced automation capabilities.",
    logo: 'ns1.svg',
    validate: true,
    inputs: [
      {
        name: 'username',
        value: 'ns1',
        hidden: true,
      },
      {
        name: 'value',
        value: 'ns1',
        hidden: true,
      },
      {
        label: 'API Key',
        value: '',
        placeholder: 'examplejKg34KdVdfg61',
        name: 'ns1_api_key',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          url: 'https://www.ibm.com/docs/en/ns1-connect?topic=keys-create-api-key',
          text: 'Learn more',
        },
      },
    ],
  },
  gcp: {
    id: Integration.gcp,
    name: 'Google Cloud Platform',
    description:
      'Discover and monitor assets hosted within your GCP environment, providing visibility into cloud infrastructure and identifying potential vulnerabilities.',
    logo: 'gcloud.svg',
    multiple: true,
    Markup: GoogleCloud,
    hideFooter: true,
  },
  azure: {
    id: Integration.azure,
    name: 'Azure',
    description:
      'Uncover assets and assess security across your Azure environment, enhancing visibility into infrastructure and cloud-based risks.',
    logo: 'azure.svg',
    multiple: true,
    Markup: Azure,
    hideFooter: true,
  },
  azuredevops: {
    id: Integration.azuredevops,
    name: 'Azure DevOps',
    description:
      'Scan Azure DevOps repositories using your existing Azure cloud integration.',
    logo: 'azuredevops.png',
    multiple: true,
    validate: false, // Validation happens in account handler
    warning:
      'Requires Azure cloud integration. Add the service principal (Application ID from Terraform output) to Azure DevOps Organization Settings > Users with Basic access.',
    // Child integration - uses parentKey to reference parent cloud credential
    childIntegration: true,
    fallbackIntegration: Integration['azuredevops-pat'],
    inputs: [
      {
        name: 'username',
        value: 'azuredevops',
        hidden: true,
      },
      {
        label: 'Azure Cloud Integration',
        value: '',
        name: 'parentKey',
        required: true,
        type: Input.Type.SELECT,
        optionsFromAccounts: 'azure',
        info: {
          text: 'Select the Azure cloud integration to use for authentication',
        },
      },
      {
        label: 'Azure DevOps Organization or Project URL',
        value: '',
        placeholder: 'https://dev.azure.com/your-organization',
        name: 'value',
        required: true,
        info: {
          url: 'https://learn.microsoft.com/en-us/azure/devops/repos/git/',
          text: 'Enter the Azure DevOps org URL (or org/project for project-level scanning)',
        },
      },
    ],
  },
  'azuredevops-pat': {
    id: Integration['azuredevops-pat'],
    name: 'Azure DevOps (PAT)',
    description:
      'Scan Azure DevOps repositories using a Personal Access Token.',
    logo: 'azuredevops.png',
    multiple: true,
    hidden: true, // Hidden from list, accessible via fallback link
    validate: false, // Validation happens in account handler
    help: {
      href: 'https://learn.microsoft.com/en-us/azure/devops/organizations/accounts/use-personal-access-tokens-to-authenticate',
      label: 'How to: Create a Personal Access Token',
    },
    warning:
      'PAT authentication is less secure than Entra ID. Consider using Azure cloud integration instead.',
    inputs: [
      {
        name: 'username',
        value: 'azuredevops-pat',
        hidden: true,
      },
      {
        label: 'Azure DevOps Organization or Project URL',
        value: '',
        placeholder: 'https://dev.azure.com/your-organization',
        name: 'value',
        required: true,
        info: {
          url: 'https://learn.microsoft.com/en-us/azure/devops/repos/git/',
          text: 'Enter the Azure DevOps org URL (or org/project for project-level scanning)',
        },
      },
      {
        label: 'Personal Access Token',
        value: '',
        placeholder: 'Enter your PAT',
        name: 'pat',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          text: 'Create a PAT with Code (Read) scope',
          url: 'https://learn.microsoft.com/en-us/azure/devops/organizations/accounts/use-personal-access-tokens-to-authenticate',
        },
      },
    ],
  },
  'azuredevops-workitems': {
    id: Integration['azuredevops-workitems'],
    name: 'Azure DevOps Work Items',
    description:
      'Create Azure DevOps Work Items using your Azure cloud integration for authentication.',
    logo: 'azuredevops.png',
    multiple: true,
    edit: true,
    ticketing: true,
    ticketAttributeName: 'azuredevops',
    projectSearchPlaceholder: 'Search for an Azure DevOps project...',
    fallbackIntegration: Integration['azuredevops-workitems-pat'],
    fallbackDescription: "Don't have an Azure cloud integration?",
    fallbackLabel: 'Use a Personal Access Token instead →',
    help: {
      href: 'https://learn.microsoft.com/en-us/azure/devops/boards/work-items/about-work-items',
      label: 'How to: Azure DevOps Work Items',
    },
    inputs: [
      {
        name: 'username',
        value: 'azuredevops-workitems',
        hidden: true,
      },
      {
        label: 'Azure Cloud Integration',
        value: '',
        name: 'parentKey',
        required: true,
        type: Input.Type.SELECT,
        optionsFromAccounts: 'azure',
        info: {
          text: 'Select the Azure cloud integration to use for authentication',
        },
      },
      {
        label: 'Organization URL',
        value: '',
        placeholder: 'https://dev.azure.com/your-organization',
        name: 'value',
        required: true,
        info: {
          text: 'Your Azure DevOps organization URL',
        },
      },
    ],
    configuration: {
      title: 'Configure Azure DevOps Settings',
      description: 'Select project and work item type',
      inputs: [
        {
          label: 'Integration Name',
          value: '',
          placeholder: 'My Azure DevOps Integration',
          name: 'displayName',
          required: true,
        },
        {
          label: 'Project',
          value: '',
          name: 'projectKey',
          placeholder: 'Select Project',
          type: Input.Type.SELECT,
          options: [],
          info: {
            text: 'The Azure DevOps project where Work Items will be created',
          },
          required: true,
        },
        {
          label: 'Project Name',
          value: '',
          name: 'projectName',
          hidden: true,
        },
        {
          label: 'id',
          value: '',
          name: 'id',
          hidden: true,
        },
        {
          label: 'Work Item Type',
          value: '',
          name: 'issueType',
          placeholder: 'Select Work Item Type',
          dependsOn: 'projectKey',
          type: Input.Type.SELECT,
          options: [],
          info: {
            text: 'The type of Work Item to create (e.g., Bug, Task, Issue)',
          },
          required: true,
        },
        {
          label: 'Automatically Create Work Items?',
          info: {
            text: 'When enabled, Work Items will be automatically created for new Chariot vulnerabilities',
          },
          value: 'false',
          name: 'autoCreate',
          type: Input.Type.CHECKBOX,
        },
        {
          label: 'Vulnerability Severity Threshold',
          info: {
            text: 'Only vulnerabilities with severity at or above the selected threshold will automatically create Work Items',
          },
          value: 'HC',
          name: 'severities',
          type: Input.Type.SELECT,
          placeholder: 'Select Severity',
          options: options,
          required: true,
          dependsOn: 'autoCreate',
          hideTillDependsOn: true,
        },
      ],
    },
  },
  'azuredevops-workitems-pat': {
    id: Integration['azuredevops-workitems-pat'],
    name: 'Azure DevOps Work Items (PAT)',
    description:
      'Create Azure DevOps Work Items from Chariot risks using a Personal Access Token.',
    logo: 'azuredevops.png',
    multiple: true,
    edit: true,
    hidden: true, // Hidden from list, accessible via fallback link
    overrideMemberName: 'azuredevops-workitems', // Uses same provider as Entra ID variant
    ticketing: true,
    ticketAttributeName: 'azuredevops',
    projectSearchPlaceholder: 'Search for an Azure DevOps project...',
    warning:
      'PAT authentication requires manual token rotation. Consider using Azure cloud integration instead.',
    help: {
      href: 'https://learn.microsoft.com/en-us/azure/devops/boards/work-items/about-work-items',
      label: 'How to: Azure DevOps Work Items',
    },
    inputs: [
      {
        name: 'username',
        value: 'azuredevops-workitems',
        hidden: true,
      },
      {
        label: 'Organization URL',
        value: '',
        placeholder: 'https://dev.azure.com/your-organization',
        name: 'value',
        required: true,
        info: {
          text: 'Your Azure DevOps organization URL',
        },
      },
      {
        label: 'Personal Access Token',
        value: '',
        placeholder: 'Enter your PAT',
        name: 'pat',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          text: 'Create a PAT with Work Items (Read & Write) scope',
          url: 'https://learn.microsoft.com/en-us/azure/devops/organizations/accounts/use-personal-access-tokens-to-authenticate',
        },
      },
    ],
    configuration: {
      title: 'Configure Azure DevOps Settings',
      description: 'Select project and work item type',
      inputs: [
        {
          label: 'Integration Name',
          value: '',
          placeholder: 'My Azure DevOps Integration',
          name: 'displayName',
          required: true,
        },
        {
          label: 'Project',
          value: '',
          name: 'projectKey',
          placeholder: 'Select Project',
          type: Input.Type.SELECT,
          options: [],
          info: {
            text: 'The Azure DevOps project where Work Items will be created',
          },
          required: true,
        },
        {
          label: 'Project Name',
          value: '',
          name: 'projectName',
          hidden: true,
        },
        {
          label: 'id',
          value: '',
          name: 'id',
          hidden: true,
        },
        {
          label: 'Work Item Type',
          value: '',
          name: 'issueType',
          placeholder: 'Select Work Item Type',
          dependsOn: 'projectKey',
          type: Input.Type.SELECT,
          options: [],
          info: {
            text: 'The type of Work Item to create (e.g., Bug, Task, Issue)',
          },
          required: true,
        },
        {
          label: 'Automatically Create Work Items?',
          info: {
            text: 'When enabled, Work Items will be automatically created for new Chariot vulnerabilities',
          },
          value: 'false',
          name: 'autoCreate',
          type: Input.Type.CHECKBOX,
        },
        {
          label: 'Vulnerability Severity Threshold',
          info: {
            text: 'Only vulnerabilities with severity at or above the selected threshold will automatically create Work Items',
          },
          value: 'HC',
          name: 'severities',
          type: Input.Type.SELECT,
          placeholder: 'Select Severity',
          options: options,
          required: true,
          dependsOn: 'autoCreate',
          hideTillDependsOn: true,
        },
      ],
    },
  },
  crowdstrike: {
    id: Integration.crowdstrike,
    name: 'CrowdStrike Falcon',
    description:
      'Discover endpoint hosts and vulnerabilities managed by CrowdStrike Falcon, providing comprehensive visibility into your managed endpoints and their security posture.',
    logo: 'crowdstrike.svg',
    validate: true,
    inputs: [
      {
        label: 'Cloud Region',
        value: 'us-1',
        name: 'cloud_region',
        required: true,
        type: Input.Type.SELECT,
        options: [
          { label: 'US-1 (api.crowdstrike.com)', value: 'us-1' },
          { label: 'US-2 (api.us-2.crowdstrike.com)', value: 'us-2' },
          { label: 'EU-1 (api.eu-1.crowdstrike.com)', value: 'eu-1' },
          {
            label: 'US-GOV-1 (api.laggar.gcw.crowdstrike.com)',
            value: 'us-gov-1',
          },
        ],
        info: {
          text: 'Your CrowdStrike Falcon cloud region',
        },
      },
      {
        label: 'Client ID',
        value: '',
        placeholder: uuid,
        name: 'value',
        required: true,
        info: {
          text: 'OAuth2 Client ID from Falcon console',
        },
      },
      {
        label: 'Client Secret',
        value: '',
        placeholder: 'wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY',
        name: 'client_secret',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          text: 'OAuth2 Client Secret from Falcon console',
        },
      },
      {
        label: 'Hosts (Endpoint Inventory)',
        name: 'module_hosts',
        value: 'true',
        type: Input.Type.CHECKBOX,
        info: {
          text: 'Import managed endpoint devices from the Falcon Hosts API',
        },
      },
      {
        label: 'Spotlight (Vulnerabilities)',
        name: 'module_spotlight',
        value: 'true',
        type: Input.Type.CHECKBOX,
        info: {
          text: 'Import CVE vulnerabilities from the Falcon Spotlight API',
        },
      },
      {
        label: 'Shield (SaaS Security Posture)',
        name: 'module_shield',
        value: 'false',
        type: Input.Type.CHECKBOX,
        info: {
          text: 'Monitor SaaS app misconfigurations via Falcon Shield (SSPM). Requires the SaaS Security API scope.',
        },
      },
    ],
  },

  'crowdstrike-flightcontrol': {
    id: Integration['crowdstrike-flightcontrol'],
    name: 'CrowdStrike Flight Control (MSSP)',
    description:
      'Connect with a parent/master CID to automatically discover and integrate all child CIDs. Each child CID runs as its own CrowdStrike Falcon integration with the module settings configured here.',
    logo: 'crowdstrike.svg',
    validate: true,
    inputs: [
      {
        label: 'Cloud Region',
        value: 'us-1',
        name: 'cloud_region',
        required: true,
        type: Input.Type.SELECT,
        options: [
          { label: 'US-1 (api.crowdstrike.com)', value: 'us-1' },
          { label: 'US-2 (api.us-2.crowdstrike.com)', value: 'us-2' },
          { label: 'EU-1 (api.eu-1.crowdstrike.com)', value: 'eu-1' },
          {
            label: 'US-GOV-1 (api.laggar.gcw.crowdstrike.com)',
            value: 'us-gov-1',
          },
        ],
        info: {
          text: 'Your CrowdStrike Falcon cloud region',
        },
      },
      {
        label: 'Client ID',
        value: '',
        placeholder: uuid,
        name: 'value',
        required: true,
        info: {
          text: 'OAuth2 Client ID from Falcon console (parent/master CID API client)',
        },
      },
      {
        label: 'Client Secret',
        value: '',
        placeholder: 'wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY',
        name: 'client_secret',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          text: 'OAuth2 Client Secret from Falcon console',
        },
      },
      {
        name: 'flight_control',
        value: 'true',
        hidden: true,
      },
      {
        label: 'Hosts (Endpoint Inventory)',
        name: 'module_hosts',
        value: 'true',
        type: Input.Type.CHECKBOX,
        info: {
          text: 'Import managed endpoint devices from each child CID',
        },
      },
      {
        label: 'Spotlight (Vulnerabilities)',
        name: 'module_spotlight',
        value: 'true',
        type: Input.Type.CHECKBOX,
        info: {
          text: 'Import CVE vulnerabilities from each child CID',
        },
      },
      {
        label: 'Shield (SaaS Security Posture)',
        name: 'module_shield',
        value: 'false',
        type: Input.Type.CHECKBOX,
        info: {
          text: 'Monitor SaaS app misconfigurations via Falcon Shield for each child CID',
        },
      },
    ],
  },

  cortex: {
    id: Integration['cortex'],
    name: 'Palo Alto Cortex Cloud',
    description:
      'Import incidents, alerts, and endpoint data from Palo Alto Cortex Cloud to enhance threat detection and response capabilities with extended detection and response insights.',
    logo: 'paloalto.svg',
    validate: true,
    inputs: [
      {
        label: 'Cortex Cloud FQDN',
        value: '',
        placeholder: 'your-tenant.xdr.us.paloaltonetworks.com',
        name: 'value',
        required: true,
        info: {
          text: 'The fully qualified domain name for your Cortex Cloudtenant (without https://)',
        },
      },
      {
        label: 'API Key ID',
        value: '',
        placeholder: '1234567',
        name: 'api_key_id',
        required: true,
        info: {
          text: 'The API Key ID from Settings > Configurations > Integrations > API Keys > New Key',
        },
      },
      {
        label: 'API Key',
        value: '',
        placeholder: '',
        name: 'api_key',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          text: 'The API Key from Settings > Configurations > Integrations > API Keys > New Key',
        },
      },
    ],
  },
  gitlab: {
    id: Integration.gitlab,
    name: 'GitLab',
    help: {
      href: 'https://docs.praetorian.com/articles/32044845804315-gitlab',
      label: 'How to: Source Code Managers - GitLab',
    },
    description:
      'Enhance software development and delivery with GitLab’s comprehensive DevOps platform, offering source code management, CI/CD, and integrated security tools. Supports both gitlab.com and self-hosted instances.',
    logo: 'gitlab.svg',
    multiple: true,
    validate: true,
    inputs: [
      {
        name: 'username',
        value: 'gitlab',
        hidden: true,
      },
      {
        label: 'Personal Access Token (PAT)',
        value: '',
        placeholder: 'glpat-123456abcdefg-123456abcdefg',
        name: 'pat',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          url: 'https://docs.gitlab.com/ee/user/profile/personal_access_tokens.html',
          text: 'Learn more',
        },
      },
      {
        label: '(Parent) GitLab Group URL',
        value: '',
        placeholder:
          'https://gitlab.com/gitlab-org or https://gitlab.example.com/mygroup',
        pattern: '^http(s)?://.+$',
        name: 'value',
        required: true,
        info: {
          url: 'https://docs.gitlab.com/ee/user/group/',
          text: 'Enter the full URL to your GitLab group. For self-hosted instances, use your GitLab server URL.',
        },
      },
    ],
  },
  nessus: {
    id: Integration.nessus,
    name: 'Nessus Professional',
    description:
      'Perform detailed vulnerability assessments with industry-leading scanning capabilities to uncover and mitigate risks.',
    logo: 'nessus.svg',
    validate: true,
    help: {
      href: 'https://docs.praetorian.com/articles/28054164449435-tenable-nessus',
      label: 'How to: Asset Ingestion - Nessus',
    },
    inputs: [
      {
        name: 'username',
        value: 'nessus',
        hidden: true,
      },
      {
        label: 'API URL',
        value: '',
        placeholder: 'https://nessus.example.com',
        name: 'url',
        required: true,
      },
      {
        label: 'Access Key',
        value: '',
        placeholder:
          '15951d2dbbb1e3bf2afe638ff9c78cdf364c2f7020954529309fbc7c40f3aa02',
        name: 'key',
        required: true,
        type: Input.Type.PASSWORD,
      },
      {
        label: 'Secret Key',
        value: '',
        placeholder:
          '0fbda7c50d239d475c72283897e644cc0606a0ceab6a4f8844a01880669e7039',
        name: 'secret',
        required: true,
        type: Input.Type.PASSWORD,
      },
      {
        label: 'Import Assets',
        name: 'importAssets',
        value: 'true',
        type: Input.Type.CHECKBOX,
      },
      {
        label: 'Import Vulnerabilities',
        name: 'importVulnerabilities',
        value: 'true',
        type: Input.Type.CHECKBOX,
      },
    ],
  },
  tenablevm: {
    id: Integration.tenablevm,
    logo: 'tenable.svg',
    name: 'Tenable VM',
    description:
      'Discover, assess, and manage vulnerabilities across your attack surface with dynamic insights and cloud-native scalability.',
    validate: true,
    inputs: [
      {
        name: 'username',
        value: 'tenablevm',
        hidden: true,
      },
      {
        label: 'Tenable VM URL',
        name: 'value',
        value: '',
        placeholder: 'https://cloud.tenable.com',
        required: true,
      },
      {
        label: 'Access Key',
        name: 'access_key',
        value: '',
        placeholder:
          '91a24bfe78365214789c405d2346b8f3e85d64a19732ebc8951e3a443251b807',
        required: true,
        type: Input.Type.PASSWORD,
      },
      {
        label: 'Secret Key',
        name: 'secret_key',
        value: '',
        placeholder:
          'f847213a56c9e44d82aa95b7c4671efdb28ae31f9dd684772153c8f445d67291',
        required: true,
        type: Input.Type.PASSWORD,
      },
      {
        label: 'Import Assets',
        name: 'importAssets',
        value: 'true',
        type: Input.Type.CHECKBOX,
      },
      {
        label: 'Import Vulnerabilities',
        name: 'importVulnerabilities',
        value: 'true',
        type: Input.Type.CHECKBOX,
      },
    ],
  },
  vercel: {
    id: Integration.vercel,
    logo: 'vercel.svg',
    name: 'Vercel',
    description:
      'Import projects and deployment data from Vercel for asset discovery and monitoring.',
    validate: true,
    inputs: [
      {
        name: 'username',
        value: 'vercel',
        hidden: true,
      },
      {
        name: 'value',
        value: 'vercel',
        hidden: true,
      },
      {
        label: 'Access Token',
        name: 'token',
        value: '',
        placeholder: 'Enter your Vercel access token',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          url: 'https://vercel.com/account/tokens',
          text: 'Create a token in Vercel Account Settings > Tokens',
        },
      },
      {
        label: 'Team ID',
        name: 'team_id',
        value: '',
        placeholder: 'Enter your Vercel Team ID (optional)',
        required: false,
        info: {
          url: 'https://vercel.com/docs/accounts/team-members-and-roles',
          text: 'Required for team accounts. Find your Team ID in Team Settings > General.',
        },
      },
    ],
  },
  zulip: {
    id: Integration.zulip,
    name: 'Zulip',
    description:
      'Sends notifications to your Zulip channels when new vulnerabilities are discovered.',
    logo: 'zulip.svg',
    inputs: [
      {
        name: 'username',
        value: 'zulip',
        hidden: true,
      },
      {
        label: 'Webhook URL',
        value: '',
        placeholder:
          'https://yourZulipDomain.zulipchat.com/api/v1/external/slack_incoming',
        name: 'webhook',
        required: true,
        info: {
          url: 'https://zulip.com/integrations/doc/slack_incoming',
          text: 'Learn more',
        },
      },
      {
        label: 'Severity',
        value: 'MHC',
        name: 'severities',
        required: true,
        type: Input.Type.SELECT,
        options: options,
        info: severityInfo,
      },
    ],
  },
  invicti: {
    id: Integration.invicti,
    name: 'Invicti',
    description:
      'Imports vulnerability scan results from Invicti, providing insights into web application security.',
    logo: 'invicti.jpeg',
    multiple: false,
    validate: true,
    inputs: [
      {
        name: 'username',
        value: 'invicti',
        hidden: true,
      },
      {
        label: 'API Base URL',
        value: '',
        placeholder: 'https://www.netsparkercloud.com/api/1.0',
        name: 'value',
        required: true,
        info: {
          text: 'Your Invicti API base URL including the API version path (e.g., https://www.netsparkercloud.com/api/1.0 or your self-hosted equivalent).',
        },
      },
      {
        label: 'User ID',
        value: '',
        placeholder: 'Your Invicti User ID',
        name: 'user_id',
        required: true,
        info: {
          text: 'Found on your Invicti account page.',
        },
      },
      {
        label: 'API Token',
        value: '',
        placeholder: 'Your Invicti API Token',
        name: 'api_token',
        type: Input.Type.PASSWORD,
        required: true,
        info: {
          text: 'Found on your Invicti account page. This will be used for Basic Authentication.',
        },
      },
    ],
  },
  teams: {
    id: Integration.teams,
    name: 'Microsoft Teams',
    description:
      'Provides real-time notifications in Microsoft Teams when new risks are identified.',
    logo: 'teams.svg',
    inputs: [
      {
        name: 'username',
        value: 'teams',
        hidden: true,
      },
      {
        label: 'Webhook URL',
        value: '',
        placeholder: 'https://xxxxxxx.logic.azure.com:443/workflows/xxxxxxx',
        name: 'webhook',
        required: true,
        info: {
          url: 'https://support.microsoft.com/en-us/office/post-a-workflow-when-a-webhook-request-is-received-in-microsoft-teams-8ae491c7-0394-4861-ba59-055e33f75498',
          text: 'Learn more',
        },
      },
      {
        label: 'Severity',
        value: 'MHC',
        name: 'severities',
        required: true,
        type: Input.Type.SELECT,
        options: options,
        info: severityInfo,
      },
    ],
  },
  cloudflare: {
    id: Integration.cloudflare,
    name: 'Cloudflare DNS',
    description:
      'Enumerate and ingest assets from Cloudflare DNS records, Firewall rulesets, and more.',
    logo: 'cloudflare.svg',
    validate: true,
    inputs: [
      {
        name: 'username',
        value: 'cloudflare',
        hidden: true,
      },
      {
        name: 'value',
        value: 'cloudflare',
        hidden: true,
      },
      {
        label: 'API Token',
        value: '',
        placeholder: 'examplejKg34KdVdfg61',
        name: 'token',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          url: 'https://developers.cloudflare.com/fundamentals/api/get-started/create-token/',
          text: 'Learn more',
        },
      },
    ],
  },
  'cloudflare-waf': {
    id: Integration.cloudflarewaf,
    name: 'Cloudflare WAF',
    description:
      'Automatically whitelist Chariot scanner traffic in your Cloudflare WAF to prevent blocking during security scans.',
    logo: 'cloudflare.svg',
    validate: true,
    inputs: [
      {
        name: 'username',
        value: 'cloudflare-waf',
        hidden: true,
      },
      {
        name: 'value',
        value: 'cloudflare-waf',
        hidden: true,
      },
      {
        name: 'token',
        label: 'API Token',
        value: '',
        placeholder: 'a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6q7r8s9t0',
        type: Input.Type.PASSWORD,
        required: true,
        info: {
          text: 'Required permissions: Zone WAF Edit, Zone DNS Read, Zone Settings Read',
          url: 'https://developers.cloudflare.com/fundamentals/api/get-started/create-token/',
        },
      },
      {
        name: 'zones',
        label: 'Zones (Optional)',
        value: '',
        placeholder: 'example.com\ntest.com\napp.example.com',
        type: Input.Type.TEXT_AREA,
        multilineList: true,
        required: false,
        info: {
          text: 'Enter zone names (one per line). Leave empty to apply to all zones.',
        },
      },
    ],
  },
  fastly: {
    id: Integration.fastly,
    name: 'Fastly',
    description:
      'Import domain names from your Fastly CDN and WAF services for asset discovery, and optionally whitelist Guard static IP ranges in WAF services.',
    logo: 'fastly.svg',
    validate: true,
    inputs: [
      {
        name: 'value',
        value: 'fastly',
        hidden: true,
      },
      {
        name: 'token',
        label: 'API Token',
        value: '',
        placeholder: 'a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6q7r8s9t0',
        type: Input.Type.PASSWORD,
        required: true,
        info: {
          text: 'Requires a token with the appropriate scope and engineer role.',
          url: 'https://www.fastly.com/documentation/guides/account-info/user-access-and-control/configuring-user-roles-and-permissions/',
        },
      },
      {
        label: 'Asset Discovery (CDN and WAF)',
        name: 'asset_discovery',
        value: 'true',
        type: Input.Type.CHECKBOX,
        info: {
          text: "Permission requirement: scope is 'global:read' and role is Engineer",
        },
      },
      {
        label: 'Whitelist Guard static IP ranges in WAF',
        name: 'waf_whitelisting',
        value: 'false',
        type: Input.Type.CHECKBOX,
        info: {
          text: "Permission requirement: scope is 'global' and role is Engineer",
        },
      },
    ],
  },
  digitalocean: {
    id: Integration.digitalocean,
    name: 'DigitalOcean',
    description:
      'Monitor assets hosted in your DigitalOcean environment to enhance visibility and address potential vulnerabilities in cloud infrastructure.',
    logo: 'digitalocean.svg',
    inputs: [
      {
        label: 'Personal Access Token (PAT)',
        value: '',
        placeholder: 'dop_v1_',
        name: 'token',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          url: 'https://www.digitalocean.com/docs/apis-clis/api/create-personal-access-token/',
          text: 'Learn more',
        },
      },
    ],
  },
  gchat: {
    id: Integration.gchat,
    name: 'Google Chat',
    description:
      'Sends Google Chat notifications for new risk detections, enabling rapid response.',
    logo: 'gchat.svg',
    inputs: [
      {
        name: 'username',
        value: 'gchat',
        hidden: true,
      },
      {
        label: 'Webhook URL',
        value: '',
        placeholder:
          'https://chat.googleapis.com/v1/spaces/SPACE/messages?key=KEY&token=TOKEN',
        name: 'webhook',
        required: true,
        info: {
          url: 'https://developers.google.com/workspace/chat/quickstart/webhooks#create_a_webhook',
          text: 'Learn more',
        },
      },
      {
        label: 'Severity',
        value: 'MHC',
        name: 'severities',
        required: true,
        type: Input.Type.SELECT,
        options: options,
        info: severityInfo,
      },
    ],
  },
  burpsuite: {
    id: Integration.burpsuite,
    name: 'Burp Enterprise',
    description:
      'Imports vulnerability scan results from Burp Suite Enterprise, providing insights into web application security.',
    logo: 'burp.svg',
    validate: true,
    inputs: [
      {
        name: 'username',
        value: 'burpsuite',
        hidden: true,
      },
      {
        label: 'Instance URL',
        value: '',
        placeholder: 'xxxxxxxx.portswigger.cloud',
        name: 'value',
        required: true,
      },
      {
        label: 'API Token',
        value: '',
        placeholder: '',
        info: {
          url: 'https://portswigger.net/burp/documentation/enterprise/user-guide/api-documentation/create-api-user',
          text: 'Learn more',
        },
        name: 'portswigger',
        type: Input.Type.PASSWORD,
      },
    ],
  },
  checkmarx: {
    id: Integration.checkmarx,
    logo: 'checkmarx.svg',
    name: 'Checkmarx SAST',
    description:
      'Import SAST scan results from Checkmarx to track application security vulnerabilities.',
    validate: true,
    inputs: [
      {
        name: 'username',
        value: 'checkmarx',
        hidden: true,
      },
      {
        label: 'Checkmarx One URL',
        name: 'value',
        value: '',
        placeholder: 'https://ast.checkmarx.net',
        required: true,
      },
      {
        label: 'Refresh Token',
        name: 'api_key',
        value: '',
        placeholder: 'Enter your Checkmarx refresh token',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          url: 'https://docs.checkmarx.com/en/34965-68618-generating-an-api-key.html',
          text: 'Generate a refresh token in Settings > Identity and Access Management',
        },
      },
    ],
  },
  insightvm: {
    id: Integration.insightvm,
    name: 'Rapid7 InsightVM',
    description:
      'Import assets and risks from your Rapid7 InsightVM account via .xml set up an API integration for continuous monitoring.',
    logo: 'rapid7.svg',
    inputs: [
      {
        name: 'username',
        value: 'insightvm',
        hidden: true,
      },
      {
        label: 'Security Console URL',
        name: 'value',
        value: '',
        placeholder: 'https://0.0.0.0:3780',
        required: true,
      },
      {
        label: 'Username',
        name: 'user',
        value: '',
        placeholder: 'admin',
        required: true,
      },
      {
        label: 'Password',
        name: 'password',
        value: '',
        placeholder: 't*VK6aJ8y3rBxn7ZNPaK_yti_iy3AY',
        required: true,
        type: Input.Type.PASSWORD,
      },
      {
        label: 'Import Assets',
        name: 'importAssets',
        value: 'true',
        type: Input.Type.CHECKBOX,
      },
      {
        label: 'Import Vulnerabilities',
        name: 'importVulnerabilities',
        value: 'true',
        type: Input.Type.CHECKBOX,
      },
    ],
  },
  axonius: {
    id: Integration.axonius,
    name: 'Axonius',
    description:
      'Gain complete visibility into your IT and security assets by aggregating data from existing solutions to uncover gaps and enforce security policies.',
    logo: 'axonius.svg',
    validate: true,
    help: {
      href: 'https://docs.axonius.com/docs/axonius-rest-api#getting-an-api-key-and-api-secret',
      label: 'Axonius - Getting an API Key and API Secret',
    },
    inputs: [
      {
        name: 'username',
        value: 'axonius',
        hidden: true,
      },
      {
        label: 'Axonius Instance URL',
        name: 'value',
        value: '',
        placeholder: 'https://customer.axonius.com',
        required: true,
      },
      {
        label: 'API Key',
        name: 'key',
        value: '',
        placeholder: '',
        required: true,
        type: Input.Type.PASSWORD,
      },
      {
        label: 'API Secret',
        name: 'secret',
        value: '',
        placeholder: '',
        required: true,
        type: Input.Type.PASSWORD,
      },
    ],
  },
  sharphound: {
    id: Integration.sharphound,
    name: 'Sharphound',
    description: 'Upload Sharphound dumps collected offline.',
    logo: 'bloodhound.svg',
    inputs: [],
  },
  qualys: {
    id: Integration.qualys,
    name: 'Qualys',
    description:
      'Streamline vulnerability management with continuous scanning and prioritization to protect your global IT assets.',
    logo: 'qualys.svg',
    validate: true,
    inputs: [
      {
        name: 'username',
        value: 'qualys',
        hidden: true,
      },
      {
        label: 'Qualys API Server URL',
        name: 'value',
        value: '',
        placeholder: 'https://qualysapi.qualys.com',
        required: true,
        info: {
          url: 'https://www.qualys.com/platform-identification/',
          text: 'Identify your Qualys platform',
        },
      },
      {
        label: 'Username',
        name: 'user',
        value: '',
        placeholder: 'quays_ab1',
        required: true,
      },
      {
        label: 'Password',
        name: 'password',
        value: '',
        placeholder: 'NV8_d9m8wF3st3hB',
        required: true,
        type: Input.Type.PASSWORD,
      },
      {
        label: 'Import Assets',
        name: 'importAssets',
        value: 'true',
        type: Input.Type.CHECKBOX,
      },
      {
        label: 'Import Vulnerabilities',
        name: 'importVulnerabilities',
        value: 'true',
        type: Input.Type.CHECKBOX,
      },
    ],
  },
  freshdesk: {
    id: Integration.freshdesk,
    logo: 'freshdesk.svg',
    name: 'Freshdesk',
    description:
      'Enhance customer support and IT service delivery with Freshdesk, providing an intuitive ticketing system, automation, and multichannel support features.',
    inputs: [
      {
        name: 'username',
        value: 'freshdesk',
        hidden: true,
      },
      {
        label: 'Account',
        name: 'value',
        value: '',
        placeholder: 'Praetorian',
        required: true,
      },
      {
        label: 'Freshdesk URL',
        name: 'url',
        value: '',
        placeholder: 'https://praetorian.freshdesk.com',
        required: true,
      },
      {
        label: 'API Key',
        name: 'apiKey',
        value: '',
        placeholder: 'kM8nXp2jHt5vLzWqRyBc',
        required: true,
        type: Input.Type.PASSWORD,
      },
      {
        label: 'Severity',
        value: 'MHC',
        name: 'severities',
        required: true,
        type: Input.Type.SELECT,
        options: options,
        info: severityInfo,
      },
    ],
  },
  freshservice: {
    id: Integration.freshservice,
    logo: 'freshservice.svg',
    name: 'Freshservice',
    description:
      'Streamline IT service management with Freshservice, offering comprehensive ITSM capabilities including incident management, asset tracking, and automated workflows.',
    multiple: true,
    validate: true,
    inputs: [
      {
        label: 'Freshservice URL',
        name: 'value',
        value: '',
        placeholder: 'https://praetorian.freshservice.com',
        required: true,
        info: {
          text: 'Your Freshservice domain URL',
        },
      },
      {
        label: 'API Key',
        name: 'apiKey',
        value: '',
        placeholder: 'kM8nXp2jHt5vLzWqRyBc',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          url: 'https://support.freshservice.com/support/solutions/articles/50000000306-where-do-i-find-my-api-key',
          text: 'Learn more about API keys',
        },
      },
    ],
  },
  bitbucket: {
    id: Integration.bitbucket,
    logo: 'bitbucket.svg',
    name: 'Bitbucket',
    description:
      'Collaborate on code development with Bitbucket’s Git-based repository management, featuring built-in CI/CD pipelines, code reviews, and Jira integration for seamless workflows.',
    help: {
      href: 'https://docs.praetorian.com/articles/38367891777435-bitbucket',
      label: 'How to: Source Code Managers - Bitbucket',
    },
    multiple: true,
    validate: true,
    inputs: [
      {
        name: 'username',
        value: 'bitbucket',
        hidden: true,
      },
      {
        label: 'Workspace Token',
        value: '',
        placeholder: 'your-oauth-token',
        name: 'access_token',
        required: true,
        password: true,
        type: Input.Type.TEXT_AREA,
        info: {
          url: 'https://support.atlassian.com/bitbucket-cloud/docs/workspace-access-tokens/',
          text: 'Learn more',
        },
      },
      {
        label: 'Bitbucket Workspace URL',
        value: '',
        placeholder: 'bitbucket.org/workspace',
        pattern: '^(http(s)?://)?[^/]+/[^/]+$',
        name: 'value',
        required: true,
        info: {
          url: 'https://support.atlassian.com/bitbucket-cloud/docs/what-is-a-workspace/',
          text: 'Learn more',
        },
      },
    ],
    Markup: BitbucketAuthInstructions,
  },
  wiz: {
    id: Integration.wiz,
    logo: 'wiz.svg',
    name: 'Wiz',
    description:
      'Identify and prioritize cloud security risks with comprehensive visibility and context across cloud environments and workloads.',
    multiple: true,
    validate: true,
    inputs: [
      {
        label: 'API Endpoint URL',
        name: 'value',
        value: '',
        placeholder: 'https://api.us1.app.wiz.io/graphql',
        required: true,
        info: {
          url: 'https://docs.wiz.io/wiz-docs/docs/using-the-wiz-api#the-graphql-endpoint',
          text: 'Learn more',
        },
      },
      {
        label: 'Client ID',
        name: 'client_id',
        value: '',
        placeholder: 'kxmrpqwzn4b83y12hfj9vsdlctkiu5cbt5bwm3mp24g8nsusdkfxv',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          url: 'https://docs.wiz.io/wiz-docs/docs/service-accounts-settings?lng=en#add-a-service-account',
          text: 'Learn more',
        },
      },
      {
        label: 'Client Secret',
        name: 'client_secret',
        value: '',
        placeholder:
          'KRNwj8mPXkY4HVL2xZEtQnWcMBsAhfvD9yUg5JCr3bGdTq7i6SxzuFkpNnY1K4m2',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          url: 'https://docs.wiz.io/wiz-docs/docs/service-accounts-settings?lng=en#add-a-service-account',
          text: 'Learn more',
        },
      },
    ],
  },
  godaddy: {
    id: Integration.godaddy,
    logo: 'godaddy.svg',
    name: 'GoDaddy DNS',
    description:
      "Simplify domain management with GoDaddy's reliable managed DNS service, providing fast resolution speeds and intuitive configuration for businesses of all sizes.",
  },
  gsuite: {
    id: Integration.gsuite,
    logo: 'gsuite.svg',
    name: 'Google Suite',
  },
  shodan: {
    id: Integration.shodan,
    logo: 'shodan.svg',
    description:
      'Import monitored networks, hostnames, domains, and known vulnerabilities from your Shodan account for asset discovery and risk enrichment.',
    name: 'Shodan',
    validate: true,
    inputs: [
      { name: 'username', value: 'shodan', hidden: true },
      {
        name: 'value',
        label: 'API Key',
        value: '',
        placeholder: 'Enter Shodan API key',
        required: true,
        type: Input.Type.PASSWORD,
      },
    ],
  },
  censys_asm: {
    id: Integration.censys_asm,
    logo: 'censys.svg',
    name: 'Censys ASM (Preview)',
    description:
      'Import attack surface data and risks from Censys ASM for comprehensive asset discovery and monitoring of risks.',
    validate: true,
    multiple: true,
    inputs: [
      {
        name: 'username',
        value: 'censys_asm',
        hidden: true,
      },
      {
        label: 'Censys ASM Workspace Name',
        name: 'value',
        value: '',
        placeholder: 'The name given for the Censys workspace',
        required: true,
        info: {
          text: 'A label to identify which Censys ASM workspace this connection is for',
        },
      },
      {
        label: 'ASM API Key',
        name: 'api_key',
        value: '',
        placeholder: 'Enter your Censys ASM API key',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          url: 'https://app.censys.io/integrations',
          text: 'Find your ASM API key on the Integrations page',
        },
      },
      {
        label: 'Import Vulnerabilities',
        name: 'importVulnerabilities',
        value: 'true',
        type: Input.Type.CHECKBOX,
      },
      {
        label: 'Import Assets',
        name: 'importAssets',
        value: 'false',
        type: Input.Type.CHECKBOX,
      },
    ],
  },
  cycognito: {
    id: Integration.cycognito,
    logo: 'cycognito.svg',
    name: 'CyCognito (Preview)',
    description:
      'Import attack surface assets and issues from CyCognito for continuous discovery and monitoring.',
    validate: true,
    inputs: [
      {
        name: 'username',
        value: 'cycognito',
        hidden: true,
      },
      {
        label: 'CyCognito API Base URL',
        name: 'value',
        value: '',
        placeholder: 'https://api.platform.cycognito.com',
        required: true,
        info: {
          text: 'The API base URL for your CyCognito tenant. Find this in your CyCognito console under Settings > API, or contact your CyCognito administrator. Most tenants use https://api.platform.cycognito.com; US-region tenants use https://api.us-platform.cycognito.com.',
        },
      },
      {
        label: 'API Key',
        name: 'api_key',
        value: '',
        placeholder: 'Enter your CyCognito API key',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          url: 'https://docs.cycognito.com/docs/api-key',
          text: 'Generate an API key in the CyCognito console',
        },
      },
      {
        label: 'Import Vulnerabilities',
        name: 'importVulnerabilities',
        value: 'true',
        type: Input.Type.CHECKBOX,
      },
      {
        label: 'Import Assets',
        name: 'importAssets',
        value: 'false',
        type: Input.Type.CHECKBOX,
      },
    ],
  },
  flexera: {
    id: Integration.flexera,
    logo: 'flexera.svg',
    name: 'Flexera One (Preview)',
    description:
      'Import internal IT asset inventory for use in internal coverage.',
    validate: true,
    multiple: true,
    inputs: [
      {
        name: 'username',
        value: 'flexera',
        hidden: true,
      },
      {
        label: 'Flexera One Organization ID',
        name: 'value',
        value: '',
        placeholder: 'e.g., 60073',
        required: true,
        info: {
          text: 'Your Flexera One organization ID. Find it in the Flexera One console URL after /orgs/, or under Administration > Organization.',
        },
      },
      {
        label: 'Client ID',
        name: 'client_id',
        value: '',
        placeholder: 'Enter your Flexera service-account client ID',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          url: 'https://docs.flexera.com/flexera/EN/FlexeraAPI/APIKeyConcepts.htm',
          text: 'Create a Flexera service account under Administration > Identity & Access Management > Service Accounts.',
        },
      },
      {
        label: 'Client Secret',
        name: 'client_secret',
        value: '',
        placeholder: 'e.g., 66f165a37e2e5038363556c1',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          text: 'Shown once when the service account is created. Store it securely.',
        },
      },
      {
        label: 'Region',
        name: 'region',
        value: 'nam',
        required: true,
        type: Input.Type.SELECT,
        options: [
          { label: 'North America (api.flexera.com)', value: 'nam' },
          { label: 'Europe (api.flexera.eu)', value: 'eu' },
          { label: 'Asia-Pacific (api.flexera.au)', value: 'apac' },
        ],
        info: {
          text: 'The Flexera data region your tenant is hosted in.',
        },
      },
    ],
  },
  securitytrails: {
    id: Integration.securitytrails,
    logo: 'securitytrails.svg',
    name: 'SecurityTrails',
    description:
      "Gain visibility into domain and IP history with SecurityTrails' passive DNS data, empowering threat intelligence, asset discovery, and security investigations.",
  },
  xpanse: {
    id: Integration.xpanse,
    logo: 'xpanse.png',
    name: 'Xpanse',
    description:
      'Discover and manage your attack surface, uncover shadow IT, and prioritize remediation efforts with automated external asset mapping and risk analysis.',
    multiple: true,
    validate: true,
    inputs: [
      {
        label: 'API FQDN',
        name: 'value',
        value: '',
        placeholder: 'xpanse.paloaltonetworks.com',
        required: true,
        info: {
          url: 'https://docs-cortex.paloaltonetworks.com/r/Cortex-Xpanse-REST-API/Get-Started-with-Cortex-Xpanse-APIs',
          text: 'The FQDN is a unique host and domain name associated with each tenant',
        },
      },
      {
        label: 'API Key Type',
        name: 'api_key_type',
        value: 'standard',
        required: true,
        type: Input.Type.SELECT,
        options: [
          { label: 'Standard', value: 'standard' },
          { label: 'Advanced', value: 'advanced' },
        ],
        info: {
          url: 'https://docs-cortex.paloaltonetworks.com/r/Cortex-Xpanse-REST-API/Get-Started-with-Cortex-Xpanse-APIs',
          text: 'The API Key Type can be either: Standard or Advanced',
        },
      },
      {
        label: 'API Key ID',
        name: 'api_key_id',
        value: '',
        placeholder: '12',
        required: true,
        info: {
          url: 'https://docs-cortex.paloaltonetworks.com/r/Cortex-Xpanse-REST-API/Get-Started-with-Cortex-Xpanse-APIs',
          text: 'The API Key ID is your unique token used to authenticate the API Key',
        },
      },
      {
        label: 'API Key',
        name: 'api_key',
        value: '',
        placeholder:
          'KRNwj8mPXkY4HVL2xZEtQnWcMBsAhfvD9yUg5JCr3bGdTq7i6SxzuFkpNnY1K4m2',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          url: 'https://docs-cortex.paloaltonetworks.com/r/Cortex-Xpanse-REST-API/Get-Started-with-Cortex-Xpanse-APIs',
          text: 'The API Key is your unique identifier used as the Authorization:{key} header required for authenticating API calls',
        },
      },
    ],
  },
  avalor: {
    id: Integration.avalor,
    logo: 'zscaler.png',
    name: 'Avalor (Zscaler)',
    description:
      'Achieve unparalleled risk insights with the power of all of your security data — plus your unique business logic and processes.',
    multiple: true,
    validate: true,
    inputs: [
      {
        label: 'SAS (Shared Access Signature) Token',
        name: 'sas_token',
        value: '',
        placeholder:
          'sv=2023-12-18&ss=bfqt&srt=sco&sp=rwdlacupiytfx&se=2024-12-15T18:30:15Z&st=2024-03-15T10:30:15Z&spr=https&sig=9XmKpLw4nRtYhB8vNcS3jQx5kFq2mJzP8bW4pHv3nM8%3D',
        required: true,
        type: Input.Type.PASSWORD,
      },
      {
        label: 'Storage Account Name',
        name: 'value',
        value: '',
        placeholder: 'examplestorageaccount',
        required: true,
      },
      {
        label: 'Container Name',
        name: 'container_name',
        value: '',
        placeholder: 'examplecontainer',
        required: true,
      },
      {
        label: 'Blob Name',
        name: 'blob_name',
        value: 'chariot-findings.json',
        required: true,
      },
    ],
  },
  palo: {
    id: Integration.palo,
    logo: 'paloalto.svg',
    name: 'Palo Alto Networks Panorama',
    description:
      'Centralize management of your Palo Alto Networks firewalls to streamline policy creation, enhance visibility, and enforce consistent security controls across your network.',
    validate: true,
    inputs: [
      {
        label: 'Panorama Host',
        name: 'value',
        value: '',
        placeholder: 'https://panorama.example.com',
        required: true,
      },
      {
        label: 'API Key',
        name: 'api_key',
        value: '',
        placeholder: '',
        required: true,
        type: Input.Type.PASSWORD,
      },
      {
        label: 'Device Group',
        name: 'device_group',
        value: '',
        placeholder: 'shared',
        required: false,
      },
      {
        label: 'API Version',
        name: 'api_version',
        value: 'v11.1',
        placeholder: 'v11.1',
        required: false,
      },
    ],
  },
  orca: {
    id: Integration.orca,
    logo: 'orca.svg',
    description:
      'Identify and prioritize cloud security risks across your environment through agentless scanning and contextual insights.',
    name: 'Orca',
    validate: true,
    inputs: [
      {
        label: 'Regional URL',
        name: 'value',
        value: '',
        placeholder: 'https://app.eu.orcasecurity.io/api/',
        required: true,
      },
      {
        label: 'API Token',
        name: 'token',
        value: '',
        placeholder:
          'aHR0cHM6Ly9hcHAuZXUub3JjYXNlY3VyaXR5LmlvfHxabWJXbHB1b2FEU1lnejhhUEZWWWkwd0dzVXZJZXZadw==',
        required: true,
        type: Input.Type.PASSWORD,
      },
    ],
  },
  'snyk-apprisk': {
    id: Integration['snyk-apprisk'],
    logo: 'snyk-apprisk.svg',
    description:
      'Prioritize and manage application security risks with Snyk AppRisk by discovering developer assets and correlating security coverage across your SDLC.',
    name: 'Snyk AppRisk',
    validate: true,
    inputs: [
      {
        label: 'Group ID',
        name: 'value',
        value: '',
        placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
        required: true,
      },
      {
        label: 'API Token',
        name: 'token',
        value: '',
        placeholder: 'snyk-api-token',
        required: true,
        type: Input.Type.PASSWORD,
      },
    ],
  },
  'snyk-code': {
    id: Integration.snyk_code,
    logo: 'snyk-code.svg',
    description:
      'Import SAST scan results from Snyk Code to identify and remediate application security vulnerabilities in your source code.',
    name: 'Snyk Code',
    validate: true,
    inputs: [
      {
        label: 'Group ID',
        name: 'value',
        value: '',
        placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
        required: true,
      },
      {
        label: 'API Token',
        name: 'token',
        value: '',
        placeholder: 'snyk-api-token',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          url: 'https://docs.snyk.io/snyk-api/authentication-for-api',
          text: 'Generate a Snyk API token',
        },
      },
    ],
  },
  'snyk-dast': {
    id: Integration.snyk_dast,
    logo: 'snyk-dast.svg',
    description:
      'Import DAST scan results from Snyk API & Web to identify and remediate web application and API security vulnerabilities.',
    name: 'Snyk DAST (Preview)',
    validate: false,
    inputs: [
      {
        name: 'username',
        value: 'snyk-dast',
        hidden: true,
      },
      {
        label: 'Account Identifier',
        name: 'value',
        value: '',
        placeholder: 'account-name-or-id',
        required: true,
      },
      {
        label: 'API Token',
        name: 'token',
        value: '',
        placeholder: 'snyk-dast-api-token',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          url: 'https://developers.probely.com/api/authentication',
          text: 'Generate an API key',
        },
      },
    ],
  },
  ibmcloud: {
    id: Integration.ibmcloud,
    logo: 'ibmcloud.svg',
    description:
      'Gain insights into assets and security configurations within your IBM Cloud environment to improve visibility and reduce risks.',
    name: 'IBM Cloud',
  },
  nexpose: {
    id: Integration.nexpose,
    logo: 'rapid7.svg',
    name: 'Rapid7 Nexpose',
    description:
      'Identify and remediate vulnerabilities effectively with real-time risk prioritization and actionable insights.',
  },
  jupiterone: {
    id: Integration.jupiterone,
    logo: 'jupiterone.svg',
    name: 'JupiterOne (Preview)',
    description:
      'Import cyber assets and security findings from JupiterOne for comprehensive visibility into your security posture and asset relationships.',
    validate: true,
    multiple: true,
    inputs: [
      {
        name: 'username',
        value: 'jupiterone',
        hidden: true,
      },
      {
        label: 'Account ID',
        name: 'value',
        value: '',
        placeholder: 'Enter your JupiterOne Account ID',
        required: true,
        info: {
          url: 'https://docs.jupiterone.io/api/authentication',
          text: 'Find your Account ID in JupiterOne Settings',
        },
      },
      {
        label: 'Region',
        name: 'region',
        value: 'us',
        required: true,
        type: Input.Type.SELECT,
        options: [
          { label: 'US', value: 'us' },
          { label: 'EU', value: 'eu' },
        ],
      },
      {
        label: 'API Key',
        name: 'api_key',
        value: '',
        placeholder: 'Enter your JupiterOne API key',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          url: 'https://docs.jupiterone.io/api/authentication',
          text: 'Generate an API key in JupiterOne Settings > API Keys',
        },
      },
      {
        label: 'Import Vulnerabilities',
        name: 'importVulnerabilities',
        value: 'true',
        type: Input.Type.CHECKBOX,
      },
      {
        label: 'Import Assets',
        name: 'importAssets',
        value: 'false',
        type: Input.Type.CHECKBOX,
      },
    ],
  },
  runzero: {
    id: Integration.runzero,
    logo: 'runzero.svg',
    description:
      'Quickly discover and inventory connected devices across your network with agentless scanning to identify risks and ensure comprehensive asset visibility.',
    name: 'RunZero',
  },
  'microsoft-defender': {
    validate: true,
    id: Integration['microsoft-defender'],
    logo: 'defender.svg',
    name: 'Microsoft Defender',
    description:
      'Strengthen your security posture with Microsoft Defender’s comprehensive MDR capabilities, including endpoint detection, cloud security, and threat intelligence, all integrated within the Microsoft ecosystem.',
    inputs: [
      {
        label: 'Client ID',
        name: 'value',
        value: '',
        placeholder: '1234567890',
        required: true,
      },
      {
        label: 'Client Secret',
        name: 'client_secret',
        value: '',
        placeholder: '1234567890',
        required: true,
        type: Input.Type.PASSWORD,
      },
      {
        label: 'Tenant ID',
        name: 'tenant_id',
        value: '',
        placeholder: '1234567890',
        required: true,
        type: Input.Type.PASSWORD,
      },
      {
        label: 'Import Assets',
        name: 'importAssets',
        value: 'true',
        type: Input.Type.CHECKBOX,
      },
      {
        label: 'Import Vulnerabilities',
        name: 'importVulnerabilities',
        value: 'true',
        type: Input.Type.CHECKBOX,
      },
    ],
  },
  sentinelone: {
    id: Integration.sentinelone,
    logo: 'sentinelone.svg',
    name: 'SentinelOne',
    description:
      "Protect your endpoints with SentinelOne's autonomous MDR solution, combining advanced AI, behavioral detection, and automated remediation to stop threats in real time.",
    validate: true,
    inputs: [
      {
        label: 'Management Console URL',
        value: '',
        placeholder: 'https://your-company.sentinelone.net',
        name: 'value',
        required: true,
        info: {
          text: 'Your SentinelOne Management Console URL',
        },
      },
      {
        label: 'API Token',
        value: '',
        placeholder: 'ey...',
        name: 'api_token',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          text: 'API token from Settings > Users > Service Users',
        },
      },
    ],
  },
  dnsdb: {
    id: Integration.dnsdb,
    logo: 'domaintools.svg',
    name: 'dnsdb',
    description:
      'Leverage Farsight DNSDB for comprehensive passive DNS data to uncover historical DNS resolutions, enabling in-depth threat analysis and forensic investigations.',
  },
  akamaidns: {
    id: Integration.akamaidns,
    description:
      'Connect your Akamai Edge DNS to discover and inventory all DNS records, enabling detection of dangling records and subdomain takeover risks.',
    logo: 'akamai.svg',
    name: 'Akamai DNS',
    validate: true,
    inputs: [
      {
        label: 'Akamai API Host',
        name: 'value',
        value: '',
        placeholder: 'akab-xxxxx.luna.akamaiapis.net',
        required: true,
        info: {
          text: 'Your Akamai API host from EdgeGrid credentials (hostname only, do not include https://)',
        },
      },
      {
        label: 'Client Token',
        name: 'client_token',
        value: '',
        placeholder: 'akab-xxxxx-xxxxx-xxxxx',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          text: 'EdgeGrid client token for authentication',
        },
      },
      {
        label: 'Client Secret',
        name: 'client_secret',
        value: '',
        placeholder: 'xxxxxxxxxxxxxxxxxxxxxx',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          text: 'EdgeGrid client secret for authentication',
        },
      },
      {
        label: 'Access Token',
        name: 'access_token',
        value: '',
        placeholder: 'akab-xxxxx-xxxxx-xxxxx',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          text: 'EdgeGrid access token for authentication',
        },
      },
    ],
  },

  'akamai-waf': {
    id: Integration['akamai-waf'],
    description:
      'Discover and monitor web application firewall security policies, rules, and configurations from Akamai Application Security.',
    logo: 'akamai.svg',
    name: 'Akamai WAF',
    validate: true,
    inputs: [
      {
        label: 'Akamai API Host',
        name: 'value',
        value: '',
        placeholder: 'akab-xxxxx.luna.akamaiapis.net',
        required: true,
        info: {
          text: 'Your Akamai API host found in EdgeGrid credentials',
        },
      },
      {
        label: 'Client Token',
        name: 'client_token',
        value: '',
        placeholder: 'akab-xxxxx-xxxxx-xxxxx',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          text: 'EdgeGrid client token for authentication',
        },
      },
      {
        label: 'Client Secret',
        name: 'client_secret',
        value: '',
        placeholder: 'xxxxxxxxxxxxxxxxxxxxxx',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          text: 'EdgeGrid client secret for authentication',
        },
      },
      {
        label: 'Access Token',
        name: 'access_token',
        value: '',
        placeholder: 'akab-xxxxx-xxxxx-xxxxx',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          text: 'EdgeGrid access token for authentication',
        },
      },
    ],
  },
  armorcode: {
    id: Integration.armorcode,
    logo: 'armorcode.svg',
    name: 'ArmorCode (Preview)',
    description:
      'Import application security findings from ArmorCode ASPM for unified vulnerability management.',
    validate: false,
    inputs: [
      {
        name: 'username',
        value: 'armorcode',
        hidden: true,
      },
      {
        label: 'API URL',
        name: 'value',
        value: '',
        placeholder: 'https://app.armorcode.com',
        required: true,
      },
      {
        label: 'API Key',
        name: 'api_key',
        value: '',
        placeholder: 'Enter your ArmorCode API key',
        required: true,
        type: Input.Type.PASSWORD,
      },
    ],
  },
  safebreach: {
    id: Integration.safebreach,
    logo: 'safebreach.svg',
    description:
      'Evaluate the resilience of your security controls through continuous attack simulation and security validation.',
    name: 'SafeBreach',
  },
  attackiq: {
    id: Integration.attackiq,
    logo: 'attackiq.svg',
    name: 'AttackIQ (Preview)',
    description:
      'Import attack surface assets and test results from AttackIQ for continuous monitoring.',
    validate: true,
    inputs: [
      {
        name: 'username',
        value: 'attackiq',
        hidden: true,
      },
      {
        label: 'AttackIQ Server URL',
        name: 'value',
        value: '',
        placeholder: 'https://firedrill.attackiq.com',
        required: true,
        info: {
          text: 'The base URL of your AttackIQ deployment',
        },
      },
      {
        label: 'API Token',
        name: 'api_key',
        value: '',
        placeholder: 'Enter your AttackIQ API token',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          text: 'Generate an API token in the AttackIQ console under Settings > API Tokens',
        },
      },
      {
        label: 'Import Vulnerabilities',
        name: 'importVulnerabilities',
        value: 'true',
        type: Input.Type.CHECKBOX,
      },
      {
        label: 'Import Assets',
        name: 'importAssets',
        value: 'false',
        type: Input.Type.CHECKBOX,
      },
    ],
  },
  sevco: {
    id: Integration.sevco,
    logo: 'sevco.svg',
    name: 'Sevco',
    description:
      'Unify asset data from disparate sources, enabling real-time visibility and actionable insights to identify and address gaps in security coverage.',
  },
  cymulate: {
    id: Integration.cymulate,
    logo: 'cymulate.svg',
    name: 'Cymulate (Preview)',
    description:
      'Import attack surface assets and findings from Cymulate for continuous exposure validation and security control assessment.',
    validate: true,
    inputs: [
      {
        name: 'username',
        value: 'cymulate',
        hidden: true,
      },
      {
        name: 'value',
        value: 'cymulate',
        hidden: true,
      },
      {
        label: 'API Token',
        name: 'api_key',
        value: '',
        placeholder: 'Enter your Cymulate API token',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          url: 'https://app.cymulate.com/',
          text: 'Generate an API token in the Cymulate console under Settings > API Keys',
        },
      },
      {
        label: 'Import Vulnerabilities',
        name: 'importVulnerabilities',
        value: 'true',
        type: Input.Type.CHECKBOX,
      },
      {
        label: 'Import Assets',
        name: 'importAssets',
        value: 'false',
        type: Input.Type.CHECKBOX,
      },
    ],
  },
  panther: {
    id: Integration.panther,
    logo: 'panther.svg',
    name: 'Panther (Preview)',
    description:
      'Import security alerts and detection data from Panther SIEM for comprehensive threat monitoring.',
    validate: false,
    inputs: [
      {
        name: 'username',
        value: 'panther',
        hidden: true,
      },
      {
        label: 'Panther Console URL',
        name: 'value',
        value: '',
        placeholder: 'https://your-instance.runpanther.net',
        required: true,
      },
      {
        label: 'API Token',
        name: 'api_token',
        value: '',
        placeholder: 'Enter your Panther API token',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          text: 'Create a token in Panther Console > Settings > API Tokens',
        },
      },
    ],
  },
  entraid: {
    id: Integration.entraid,
    logo: 'entraid.svg',
    name: 'Entra ID',
    description:
      'Included in our Azure integration. Connect Azure to manage Entra ID identities alongside your cloud resources.',
    hideFooter: true,
  },
  googlesso: {
    id: Integration.googlesso,
    logo: 'gsuite.svg',
    name: 'Google SSO',
    description:
      'Simplify and secure user access with Google Single Sign-On (SSO), enabling seamless authentication across integrated applications and services using a single set of credentials.',
  },
  okta: {
    validate: true,
    id: Integration.okta,
    help: {
      href: 'https://docs.praetorian.com/articles/40227495850779-okta-integration',
      label: 'How to create an Okta service app',
    },
    logo: 'okta.svg',
    name: 'Okta',
    description:
      'Discover and inventory applications in your Okta organization by fetching all configured applications and adding them as assets to your attack surface.',
    inputs: [
      {
        label: 'Okta Domain',
        name: 'domain',
        value: '',
        placeholder: 'https://your-company.okta.com',
        required: true,
      },
      {
        label: 'Client ID',
        name: 'value',
        value: '',
        placeholder: '0oau3w0bj7g2kQ5a4697',
        required: true,
      },
      {
        label: 'Private Key (PEM)',
        name: 'private_key',
        value: '',
        placeholder:
          '-----BEGIN PRIVATE KEY-----\nMIIEvQIBADANBg...\n-----END PRIVATE KEY-----',
        required: true,
        type: Input.Type.TEXT_AREA,
      },
      {
        label: 'Import Assets',
        name: 'importAssets',
        value: 'true',
        hidden: true,
        type: Input.Type.CHECKBOX,
      },
      {
        label: 'Import Vulnerabilities',
        name: 'importVulnerabilities',
        value: 'true',
        hidden: true,
        type: Input.Type.CHECKBOX,
      },
    ],
  },
  pingone: {
    id: Integration.pingone,
    help: {
      href: 'https://docs.pingidentity.com/pingone/applications/p1_applications_add_applications.html',
      label: 'How to create a PingOne SSO application',
    },
    name: 'PingOne SSO',
    description:
      'Discover and inventory applications in your Ping Identity organization by fetching all configured applications and adding them as assets to your attack surface.',
    logo: 'pingone.svg',
    validate: true,
    inputs: [
      {
        label: 'PingOne Regional URL',
        name: 'regional_url',
        value: '',
        placeholder: 'https://api.pingone.com',
        required: true,
      },
      {
        label: 'Environment ID',
        name: 'environment_id',
        value: '',
        placeholder: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
        required: true,
      },
      {
        label: 'Client ID',
        name: 'value',
        value: '',
        placeholder: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
        required: true,
      },
      {
        label: 'Client Secret',
        name: 'client_secret',
        value: '',
        placeholder: 'your-client-secret',
        required: true,
        type: Input.Type.PASSWORD,
      },
    ],
  },
  sailpoint: {
    id: Integration.sailpoint,
    logo: 'sailpoint.svg',
    name: 'SailPoint Identity Security Cloud (Preview)',
    description:
      'Discover and inventory applications and identities managed by SailPoint IdentityNow.',
    validate: false,
    inputs: [
      {
        name: 'username',
        value: 'sailpoint',
        hidden: true,
      },
      {
        label: 'Tenant URL',
        name: 'value',
        value: '',
        placeholder: 'https://your-tenant.api.identitynow.com',
        required: true,
      },
      {
        label: 'Client ID',
        name: 'client_id',
        value: '',
        placeholder: 'Enter your OAuth client ID',
        required: true,
      },
      {
        label: 'Client Secret',
        name: 'client_secret',
        value: '',
        placeholder: 'Enter your OAuth client secret',
        required: true,
        type: Input.Type.PASSWORD,
      },
    ],
  },
  imperva: {
    id: Integration.imperva,
    logo: 'imperva.svg',
    name: 'Imperva',
    description:
      "Protect your web applications and databases with Imperva's comprehensive security solutions including WAF, DDoS protection, and data security.",
    validate: true,
    inputs: [
      {
        name: 'username',
        value: 'imperva',
        hidden: true,
      },
      {
        label: 'API ID',
        name: 'value',
        value: '',
        placeholder: '123456',
        required: true,
      },
      {
        label: 'API Key',
        name: 'api_key',
        value: '',
        placeholder: 'b7e3f2a1-4c9d-4e2a-9f1a-2d3e4b5c6a7f',
        required: true,
        type: Input.Type.PASSWORD,
      },
    ],
  },
  socradar: {
    id: Integration.socradar,
    logo: 'socradar.svg',
    name: 'SOCRadar',
    description:
      "Enhance threat intelligence with SOCRadar's comprehensive cyber threat monitoring, providing real-time alerts and actionable insights into external threats.",
    inputs: [
      {
        name: 'username',
        value: 'socradar',
        hidden: true,
      },
      {
        label: 'API Key',
        name: 'api_key',
        value: '',
        placeholder: 'your-api-key',
        required: true,
        type: Input.Type.PASSWORD,
      },
    ],
  },
  recordedfuture: {
    id: Integration.recordedfuture,
    logo: 'recordedfuture.svg',
    name: 'Recorded Future',
    description:
      "Leverage Recorded Future's threat intelligence platform to proactively identify and mitigate cyber threats with real-time risk analysis and contextual insights.",
    inputs: [
      {
        name: 'username',
        value: 'recordedfuture',
        hidden: true,
      },
      {
        label: 'API Token',
        name: 'api_token',
        value: '',
        placeholder: 'your-api-token',
        required: true,
        type: Input.Type.PASSWORD,
      },
    ],
  },
  extrahop: {
    id: Integration.extrahop,
    logo: 'extrahop.svg',
    name: 'ExtraHop RevealX 360',
    description:
      'Discover network devices and security detections from ExtraHop RevealX 360 cloud platform. Provides comprehensive visibility into your network infrastructure with device discovery and hardening issue detection.',
    validate: true,
    inputs: [
      {
        label: 'API URL',
        name: 'value',
        value: '',
        placeholder: 'https://your-tenant.api.cloud.extrahop.com',
        required: true,
        info: {
          text: 'Your RevealX 360 API URL (found in Administration > API Access)',
        },
      },
      {
        label: 'Client ID',
        name: 'client_id',
        value: '',
        placeholder: 'your-client-id',
        required: true,
        info: {
          text: 'OAuth2 Client ID from RevealX 360 API credentials',
        },
      },
      {
        label: 'Client Secret',
        name: 'client_secret',
        value: '',
        placeholder: 'your-client-secret',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          text: 'OAuth2 Client Secret from RevealX 360 API credentials',
        },
      },
    ],
  },
  intune: {
    id: Integration.intune,
    logo: 'intune.svg',
    name: 'Microsoft Intune',
    description:
      'Included in our Azure integration. Connect Azure to manage Intune identities alongside your cloud resources.',
    hideFooter: true,
  },
  securityscorecard: {
    id: Integration.securityscorecard,
    logo: 'securityscorecard.svg',
    name: 'SecurityScorecard',
    description:
      'Monitor third-party security posture with SecurityScorecard ratings, risk factors, and active security issues across your portfolio of companies.',
    validate: true,
    inputs: [
      {
        name: 'username',
        value: 'securityscorecard',
        hidden: true,
      },
      {
        label: 'API Key',
        name: 'api',
        value: '',
        placeholder: 'your-api-key',
        required: true,
        type: Input.Type.PASSWORD,
      },
    ],
  },
  // Not used on the integrations page, but needed because we use AllIntegrations to determine what keywords are integrations
  'seed-import': {
    id: Integration['seed-import'],
    name: 'Seed Import',
    description: '',
    logo: '',
    inputs: [],
  },
  'cisa-ch-vulnscan-import': {
    id: Integration['cisa-ch-vulnscan-import'],
    name: 'CISA Vulnerability Scan',
    description: '',
    logo: '',
    inputs: [],
  },
  'cisa-ch-webappscan-import': {
    id: Integration['cisa-ch-webappscan-import'],
    name: 'CISA Web Application Vulnerability Scan',
    description: '',
    logo: '',
    inputs: [],
  },
  'nsa-csaas-assets-import': {
    id: Integration['nsa-csaas-assets-import'],
    name: 'NSA CSaaS Attack Surface Management (Assets)',
    description: '',
    logo: '',
    inputs: [],
  },
  'nsa-csaas-vulns-import': {
    id: Integration['nsa-csaas-vulns-import'],
    name: 'NSA CSaaS Attack Surface Management (Vulnerabilities)',
    description: '',
    logo: '',
    inputs: [],
  },
  pius_apollo: {
    id: Integration.pius_apollo,
    name: 'Apollo.io',
    description: 'Organization enrichment for domain discovery',
    logo: 'apollo.svg',
    inputs: [
      { name: 'username', value: 'pius_apollo', hidden: true },
      { name: 'value', value: 'pius_apollo', hidden: true },
      {
        name: 'api_key',
        label: 'API Key',
        value: '',
        placeholder: 'Enter Apollo.io API key',
        required: true,
        type: Input.Type.PASSWORD,
      },
      {
        name: 'api_usage_limit',
        label: 'Daily Usage Cap (API calls)',
        value: '',
        placeholder: 'e.g. 1000 — leave blank for unlimited',
        required: false,
      },
    ],
  },
  pius_binaryedge: {
    id: Integration.pius_binaryedge,
    name: 'BinaryEdge',
    description: 'Internet scanning platform for attack surface discovery',
    logo: 'binaryedge.svg',
    inputs: [
      { name: 'username', value: 'pius_binaryedge', hidden: true },
      { name: 'value', value: 'pius_binaryedge', hidden: true },
      {
        name: 'api_key',
        label: 'API Key',
        value: '',
        placeholder: 'Enter BinaryEdge API key',
        required: true,
        type: Input.Type.PASSWORD,
      },
      {
        name: 'api_usage_limit',
        label: 'Minimum Remaining Requests',
        value: '',
        placeholder: 'e.g. 100 — leave blank for unlimited',
        required: false,
      },
    ],
  },
  pius_censys: {
    id: Integration.pius_censys,
    name: 'Censys',
    description: 'Certificate and host discovery for attack surface mapping',
    logo: 'censys.svg',
    inputs: [
      { name: 'username', value: 'pius_censys', hidden: true },
      { name: 'value', value: 'pius_censys', hidden: true },
      {
        name: 'api_key',
        label: 'API Key',
        value: '',
        placeholder: 'Enter Censys API key (ID:Secret)',
        required: true,
        type: Input.Type.PASSWORD,
      },
      {
        name: 'org_id',
        label: 'Organization ID',
        value: '',
        placeholder: 'Enter Censys organization ID',
        required: true,
        type: Input.Type.PASSWORD,
      },
      {
        name: 'api_usage_limit',
        label: 'Monthly Usage Cap (credits)',
        value: '',
        placeholder: 'e.g. 200 — leave blank for unlimited',
        required: false,
      },
    ],
  },
  pius_crunchbase: {
    id: Integration.pius_crunchbase,
    name: 'Crunchbase',
    description: 'Company data for subsidiary discovery',
    logo: 'crunchbase.svg',
    inputs: [
      { name: 'username', value: 'pius_crunchbase', hidden: true },
      { name: 'value', value: 'pius_crunchbase', hidden: true },
      {
        name: 'api_key',
        label: 'API Key',
        value: '',
        placeholder: 'Enter Crunchbase API key',
        required: true,
        type: Input.Type.PASSWORD,
      },
    ],
  },
  pius_diffbot: {
    id: Integration.pius_diffbot,
    name: 'Diffbot',
    description: 'Web data extraction and knowledge graph for asset discovery',
    logo: 'diffbot.svg',
    inputs: [
      { name: 'username', value: 'pius_diffbot', hidden: true },
      { name: 'value', value: 'pius_diffbot', hidden: true },
      {
        name: 'api_key',
        label: 'API Key',
        value: '',
        placeholder: 'Enter Diffbot API key',
        required: true,
        type: Input.Type.PASSWORD,
      },
      {
        name: 'api_usage_limit',
        label: 'Minimum Remaining API Calls',
        value: '',
        placeholder: 'e.g. 100 — leave blank for unlimited',
        required: false,
      },
    ],
  },
  pius_dnsdb: {
    id: Integration.pius_dnsdb,
    name: 'DNSDB',
    description: 'Passive DNS historical database for domain intelligence',
    logo: 'dnsdb.svg',
    inputs: [
      { name: 'username', value: 'pius_dnsdb', hidden: true },
      { name: 'value', value: 'pius_dnsdb', hidden: true },
      {
        name: 'api_key',
        label: 'API Key',
        value: '',
        placeholder: 'Enter DNSDB API key',
        required: true,
        type: Input.Type.PASSWORD,
      },
      {
        name: 'api_usage_limit',
        label: 'Minimum Remaining Queries',
        value: '',
        placeholder: 'e.g. 500 — leave blank for unlimited',
        required: false,
      },
    ],
  },
  pius_opencorporates: {
    id: Integration.pius_opencorporates,
    name: 'OpenCorporates',
    description: 'Corporate registry data for organizational discovery',
    logo: 'opencorporates.svg',
    inputs: [
      { name: 'username', value: 'pius_opencorporates', hidden: true },
      { name: 'value', value: 'pius_opencorporates', hidden: true },
      {
        name: 'api_key',
        label: 'API Key',
        value: '',
        placeholder: 'Enter OpenCorporates API key',
        required: true,
        type: Input.Type.PASSWORD,
      },
      {
        name: 'api_usage_limit',
        label: 'Monthly Usage Cap (API calls)',
        value: '',
        placeholder: 'e.g. 5000 — leave blank for unlimited',
        required: false,
      },
    ],
  },
  pius_proxycurl: {
    id: Integration.pius_proxycurl,
    name: 'Proxycurl',
    description: 'LinkedIn data enrichment for contact discovery',
    logo: 'proxycurl.svg',
    inputs: [
      { name: 'username', value: 'pius_proxycurl', hidden: true },
      { name: 'value', value: 'pius_proxycurl', hidden: true },
      {
        name: 'api_key',
        label: 'API Key',
        value: '',
        placeholder: 'Enter Proxycurl API key',
        required: true,
        type: Input.Type.PASSWORD,
      },
      {
        name: 'api_usage_limit',
        label: 'Minimum Remaining Credits',
        value: '',
        placeholder: 'e.g. 100 — leave blank for unlimited',
        required: false,
      },
    ],
  },
  pius_github: {
    id: Integration.pius_github,
    name: 'GitHub',
    description:
      'GitHub personal access token for improved API rate limits during organization discovery',
    logo: 'github.svg',
    inputs: [
      { name: 'username', value: 'pius_github', hidden: true },
      { name: 'value', value: 'pius_github', hidden: true },
      {
        name: 'api_key',
        label: 'Personal Access Token',
        value: '',
        placeholder: 'Enter GitHub personal access token (PAT)',
        required: true,
        type: Input.Type.PASSWORD,
      },
      {
        name: 'api_usage_limit',
        label: 'Hourly Usage Cap (API calls)',
        value: '',
        placeholder: 'e.g. 4000 — leave blank for unlimited',
        required: false,
      },
    ],
  },
  pius_securitytrails: {
    id: Integration.pius_securitytrails,
    name: 'SecurityTrails',
    description: 'DNS and domain intelligence for asset enumeration',
    logo: 'securitytrails.svg',
    inputs: [
      { name: 'username', value: 'pius_securitytrails', hidden: true },
      { name: 'value', value: 'pius_securitytrails', hidden: true },
      {
        name: 'api_key',
        label: 'API Key',
        value: '',
        placeholder: 'Enter SecurityTrails API key',
        required: true,
        type: Input.Type.PASSWORD,
      },
      {
        name: 'api_usage_limit',
        label: 'Monthly Usage Cap (API calls)',
        value: '',
        placeholder: 'e.g. 5000 — leave blank for unlimited',
        required: false,
      },
    ],
  },
  pius_shodan: {
    id: Integration.pius_shodan,
    name: 'Shodan',
    description: 'Internet-connected device search engine for asset discovery',
    logo: 'shodan.svg',
    inputs: [
      { name: 'username', value: 'pius_shodan', hidden: true },
      { name: 'value', value: 'pius_shodan', hidden: true },
      {
        name: 'api_key',
        label: 'API Key',
        value: '',
        placeholder: 'Enter Shodan API key',
        required: true,
        type: Input.Type.PASSWORD,
      },
      {
        name: 'api_usage_limit',
        label: 'Minimum Remaining Query Credits',
        value: '',
        placeholder: 'e.g. 50 — leave blank for unlimited',
        required: false,
      },
    ],
  },
  pius_viewdns: {
    id: Integration.pius_viewdns,
    name: 'ViewDNS',
    description: 'DNS tools and reverse lookups for domain discovery',
    logo: 'viewdns.svg',
    inputs: [
      { name: 'username', value: 'pius_viewdns', hidden: true },
      { name: 'value', value: 'pius_viewdns', hidden: true },
      {
        name: 'api_key',
        label: 'API Key',
        value: '',
        placeholder: 'Enter ViewDNS API key',
        required: true,
        type: Input.Type.PASSWORD,
      },
      {
        name: 'api_usage_limit',
        label: 'Monthly Usage Cap (API calls)',
        value: '',
        placeholder: 'e.g. 5000 — leave blank for unlimited',
        required: false,
      },
    ],
  },
  pius_virustotal: {
    id: Integration.pius_virustotal,
    name: 'VirusTotal',
    description: 'Threat intelligence and domain reputation analysis',
    logo: 'virustotal.svg',
    inputs: [
      { name: 'username', value: 'pius_virustotal', hidden: true },
      { name: 'value', value: 'pius_virustotal', hidden: true },
      {
        name: 'api_key',
        label: 'API Key',
        value: '',
        placeholder: 'Enter VirusTotal API key',
        required: true,
        type: Input.Type.PASSWORD,
      },
      {
        name: 'api_usage_limit',
        label: 'Daily Usage Cap (API requests)',
        value: '',
        placeholder: 'e.g. 500 — leave blank for unlimited',
        required: false,
      },
    ],
  },
  'aws-waf': {
    id: Integration.aws_waf,
    logo: 'aws.svg',
    name: 'AWS WAF',
    description:
      'Discover and audit AWS WAF configurations, whitelist scanner IPs, and monitor firewall policy compliance.',
    validate: true,
    inputs: [
      {
        name: 'username',
        value: 'aws-waf',
        hidden: true,
      },
      {
        name: 'value',
        label: 'AWS Account ID',
        value: '',
        placeholder: '123456789012',
        required: true,
      },
      {
        name: 'role',
        label: 'IAM Role ARN',
        value: '',
        placeholder: 'arn:aws:iam::123456789012:role/ChariotWAFReader',
        required: true,
      },
      {
        name: 'externalId',
        label: 'External ID',
        value: '',
        placeholder: 'UUID from trust policy',
        required: true,
        type: Input.Type.PASSWORD,
      },
      {
        name: 'region',
        label: 'Region',
        value: '',
        placeholder: 'us-east-1',
        required: false,
      },
      {
        name: 'fmsAdmin',
        label: 'Enable Firewall Manager (Org-wide)',
        value: 'false',
        required: false,
        type: Input.Type.CHECKBOX,
      },
    ],
  },
  threatx: {
    id: Integration.threatx,
    logo: 'threatx.svg',
    name: 'ThreatX (Preview)',
    description:
      'Import domain names from your ThreatX WAF deployment for asset discovery.',
    validate: true,
    multiple: true,
    inputs: [
      {
        name: 'username',
        value: 'threatx',
        hidden: true,
      },
      {
        label: 'Integration Instance Name in ThreatX',
        name: 'value',
        value: '',
        placeholder: 'e.g., Praetorian Guard',
        required: true,
        info: {
          text: 'Enter what you named the integration instance in ThreatX',
        },
      },
      {
        label: 'API Key',
        name: 'api_key',
        value: '',
        placeholder: 'Enter your ThreatX API key',
        required: true,
        type: Input.Type.PASSWORD,
        info: {
          url: 'https://www.threatx.com/documentation/using-threatx/threatx-administrator-guide/',
          text: 'Generate an API key in Settings > API Keys',
        },
      },
    ],
  },
};

export interface IntegrationData {
  integrations: Account[];
  connectedIntegrations: Account[];
  connectedNotifications: Account[];
  jobKeys: Record<string, string>;
  requiresSetupIntegrations: Account[];
  waitlistIntegrations: Account[];
}

export const getIntegrations = (accounts: Account[]): IntegrationData => {
  const integrationsWithType = accounts
    .filter(account => account.member in AllIntegrations)
    .map(account => {
      return {
        ...account,
        displayName:
          AllIntegrations[account.member as Integration]?.name ||
          account.member,
      };
    });

  const connectedIntegrations = integrationsWithType.filter(
    int => int.value !== 'setup' && int.value !== 'waitlist'
  );

  const requiresSetupIntegrations = integrationsWithType.filter(
    int => int.value === 'setup'
  );

  const waitlistIntegrations = integrationsWithType.filter(
    int => int.value === 'waitlist' && !isIntegrationAvailable(int.member)
  );

  const jobKeys = connectedIntegrations.reduce(
    (acc, integration) => ({
      ...acc,
      [`${integration.member}#${integration.value}`]: `#job#${integration.member}#${integration.value}${integration.member === 'amazon' ? '' : '#'}`,
    }),
    {} as Record<string, string>
  );

  const connectedNotifications = connectedIntegrations.filter(integration =>
    availableNotifications.includes(integration.member as Integration)
  );

  return {
    integrations: integrationsWithType,
    connectedIntegrations,
    connectedNotifications,
    jobKeys,
    requiresSetupIntegrations,
    waitlistIntegrations,
  };
};

// Update the useIntegration hook to use new categories
export const useIntegration = (
  options?: UseExtendInfiniteQueryOptions<Account[]> & { email?: string }
) => {
  const {
    data: accounts,
    status,
    invalidate: invalidateAccounts,
  } = useMy(
    {
      resource: 'account',
    },
    options
  );

  const { integrations, ...restProps } = useMemo(
    () => getIntegrations(accounts),
    [accounts]
  );

  return {
    status,
    data: {
      ...restProps,
      integrations,
      accounts,
      invalidateAccounts,
    },
  };
};
// Helper function to check if integration is available
const isIntegrationAvailable = (integrationId: string) => {
  return Object.values(INTEGRATION_CATEGORIES).some(category =>
    category.some(integration => integration === integrationId)
  );
};
